import React, { useState, useRef } from 'react';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui-elements';
import { useAuthStore, useAuthHeaders } from '@/lib/auth-store';
import { useToast } from '@/hooks/use-toast';
import { ShopifyTargetStoreSelector } from '@/components/shopify-target-store';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import {
  Tag,
  CheckCircle,
  AlertCircle,
  Loader2,
  RotateCw,
  Search,
  List,
  Link2,
} from 'lucide-react';

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

interface VariantPriceMap { [key: string]: string }

interface RunState {
  phase: 'idle' | 'discovery' | 'scraping' | 'pushing' | 'done' | 'error';
  // discovery
  discoveryPage: number;
  // scraping
  scrapeProducts: number;
  scrapeTotal: number;
  scrapeFound: number;
  // push
  pushCurrent: number;
  pushTotal: number;
  // result
  variantPriceMap: VariantPriceMap;
  totalProducts: number;
  totalVariants: number;
  pushUpdated: number;
  pushSkipped: number;
  pushErrors: { sku: string; error: string }[];
  // error
  errorMessage: string;
}

const IDLE_STATE: RunState = {
  phase: 'idle',
  discoveryPage: 0,
  scrapeProducts: 0, scrapeTotal: 0, scrapeFound: 0,
  pushCurrent: 0, pushTotal: 0,
  variantPriceMap: {},
  totalProducts: 0, totalVariants: 0,
  pushUpdated: 0, pushSkipped: 0, pushErrors: [],
  errorMessage: '',
};

function parseVariantKey(key: string): { sku: string; options: string[] } {
  const sepIdx = key.indexOf('||');
  if (sepIdx === -1) return { sku: key, options: [] };
  return {
    sku: key.slice(0, sepIdx),
    options: key.slice(sepIdx + 2).split('|').filter(Boolean),
  };
}

function readSse(
  stream: ReadableStream<Uint8Array>,
  onEvent: (event: any) => void,
  signal: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const reader = stream.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    const pump = () => {
      if (signal.aborted) { reader.cancel(); resolve(); return; }
      reader.read().then(({ done, value }) => {
        if (done) { resolve(); return; }
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try { onEvent(JSON.parse(line.slice(6))); } catch { /* skip */ }
        }
        pump();
      }).catch(reject);
    };
    pump();
  });
}

export function PriceScrape() {
  const { token } = useAuthStore();
  const authOpts = useAuthHeaders();
  const { toast } = useToast();

  const [inputMode, setInputMode] = useState<'category' | 'urls'>('category');
  const [categoryUrl, setCategoryUrl] = useState('https://offroadanimal.com.au/bull-bars/');
  const [productUrls, setProductUrls] = useState('');
  const [storeId, setStoreId] = useState('');
  const [run, setRun] = useState<RunState>(IDLE_STATE);

  const abortRef = useRef<AbortController | null>(null);

  const { data: stores = [] } = useQuery<any[]>({
    queryKey: ['stores'],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/stores`, { headers: authOpts.headers });
      if (!res.ok) throw new Error('Failed to load stores');
      return res.json();
    },
    enabled: !!token,
  });

  const cancel = () => {
    abortRef.current?.abort();
    setRun(IDLE_STATE);
  };

  const startRun = async () => {
    if (inputMode === 'category' && !categoryUrl.trim()) {
      toast({ title: 'Enter a category URL', variant: 'destructive' }); return;
    }
    if (inputMode === 'urls') {
      const parsed = productUrls.split(/[\n,]+/).map(u => u.trim()).filter(u => u.startsWith('http'));
      if (parsed.length === 0) { toast({ title: 'Paste at least one product URL', variant: 'destructive' }); return; }
    }
    if (!storeId) { toast({ title: 'Select a Shopify store first', variant: 'destructive' }); return; }

    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setRun({ ...IDLE_STATE, phase: 'discovery' });

    // ── Phase 1: Scrape ─────────────────────────────────────────────────────
    let variantPriceMap: VariantPriceMap = {};
    let totalProducts = 0;
    let totalVariants = 0;
    let scrapeOk = false;

    try {
      const scrapeParam = inputMode === 'urls'
        ? `productUrls=${encodeURIComponent(productUrls.trim())}`
        : `categoryUrl=${encodeURIComponent(categoryUrl.trim())}`;
      const url = `${BASE}/api/scraper/price-scrape?${scrapeParam}`;
      const res = await fetch(url, { headers: authOpts.headers, signal: ctrl.signal });
      if (!res.ok || !res.body) {
        const data = await res.json().catch(() => ({ error: `Server error (${res.status})` }));
        throw new Error(data.error ?? 'Scrape failed');
      }

      await readSse(res.body, (ev) => {
        if (ev.type === 'phase') {
          setRun(r => ({ ...r, phase: ev.phase === 'discovery' ? 'discovery' : 'scraping' }));
        } else if (ev.type === 'discovery') {
          setRun(r => ({ ...r, phase: 'discovery', discoveryPage: ev.page, scrapeFound: ev.count }));
        } else if (ev.type === 'progress') {
          setRun(r => ({ ...r, phase: 'scraping', scrapeProducts: ev.current, scrapeTotal: ev.total, scrapeFound: ev.found }));
        } else if (ev.type === 'done') {
          variantPriceMap = ev.variantPriceMap;
          totalProducts   = ev.totalProducts;
          totalVariants   = ev.totalVariants;
          scrapeOk = true;
        } else if (ev.type === 'error') {
          throw new Error(ev.message);
        }
      }, ctrl.signal);

      if (ctrl.signal.aborted) return;

      if (!scrapeOk) {
        // Stream closed without a done event — proxy timeout or connection drop
        setRun(r => ({
          ...r, phase: 'error',
          errorMessage: 'Connection dropped before scrape completed. The category may have too many products — try a sub-category URL instead.',
        }));
        return;
      }
    } catch (err: any) {
      if (ctrl.signal.aborted) return;
      setRun(r => ({ ...r, phase: 'error', errorMessage: err.message ?? 'Scrape failed' }));
      return;
    }

    if (totalVariants === 0) {
      setRun(r => ({ ...r, phase: 'error', errorMessage: 'No variant prices were scraped — check the category URL.' }));
      return;
    }

    // Store scraped data so the result panel can show it
    setRun(r => ({ ...r, variantPriceMap, totalProducts, totalVariants }));

    // ── Phase 2: Push ───────────────────────────────────────────────────────
    setRun(r => ({ ...r, phase: 'pushing', pushCurrent: 0, pushTotal: totalVariants }));

    try {
      const res = await fetch(`${BASE}/api/scraper/price-push`, {
        method: 'POST',
        headers: { ...authOpts.headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeId, variantPriceMap }),
        signal: ctrl.signal,
      });
      if (!res.ok || !res.body) {
        const data = await res.json().catch(() => ({ error: `Server error (${res.status})` }));
        throw new Error(data.error ?? 'Push failed');
      }

      let pushOk = false;
      await readSse(res.body, (ev) => {
        if (ev.type === 'progress') {
          setRun(r => ({ ...r, pushCurrent: ev.current, pushTotal: ev.total }));
        } else if (ev.type === 'done') {
          setRun(r => ({
            ...r,
            phase: 'done',
            pushUpdated: ev.updated,
            pushSkipped: ev.skipped,
            pushErrors: ev.errors ?? [],
          }));
          pushOk = true;
        } else if (ev.type === 'error') {
          throw new Error(ev.message);
        }
      }, ctrl.signal);

      if (!ctrl.signal.aborted && !pushOk) {
        setRun(r => ({ ...r, phase: 'error', errorMessage: 'Connection dropped during push — some prices may have been updated. Check Shopify.' }));
      }
    } catch (err: any) {
      if (ctrl.signal.aborted) return;
      setRun(r => ({ ...r, phase: 'error', errorMessage: err.message ?? 'Push failed' }));
    }
  };

  const { phase } = run;
  const busy = phase === 'discovery' || phase === 'scraping' || phase === 'pushing';
  const idle = phase === 'idle';

  const scrapePercent = run.scrapeTotal > 0
    ? Math.min(100, Math.round((run.scrapeProducts / run.scrapeTotal) * 100))
    : 0;
  const pushPercent = run.pushTotal > 0
    ? Math.min(100, Math.round((run.pushCurrent / run.pushTotal) * 100))
    : 0;

  const previewEntries = Object.entries(run.variantPriceMap).slice(0, 30);
  const storeName = stores.find((s: any) => s.id === storeId)?.nickname ?? storeId;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6 py-8 px-4">

        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
            <Tag className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">Scrape & Update Prices</h1>
            <p className="text-sm text-muted-foreground">
              Pull RRP from a supplier category and push straight to Shopify — matched by SKU and variant options
            </p>
          </div>
        </div>

        {/* Config — always visible so you can queue the next run while reviewing results */}
        <div className="glass-panel rounded-2xl p-6 space-y-5">

          {/* Mode toggle */}
          <div className="flex items-center gap-1 p-1 bg-muted/50 rounded-xl w-fit">
            <button
              onClick={() => !busy && setInputMode('category')}
              disabled={busy}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-50",
                inputMode === 'category'
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <Link2 className="w-3.5 h-3.5" />
              Category URL
            </button>
            <button
              onClick={() => !busy && setInputMode('urls')}
              disabled={busy}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-50",
                inputMode === 'urls'
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <List className="w-3.5 h-3.5" />
              Paste URLs
            </button>
          </div>

          {inputMode === 'category' ? (
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Category URL</label>
              <input
                type="url"
                value={categoryUrl}
                onChange={e => setCategoryUrl(e.target.value)}
                disabled={busy}
                placeholder="https://supplier.com.au/category/"
                className="w-full bg-background border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-emerald-500/40 disabled:opacity-50"
              />
              <p className="text-xs text-muted-foreground">
                Tip: target a sub-category (e.g. /bull-bars/toyota/) to keep run times under 5 minutes.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Product URLs</label>
              <textarea
                value={productUrls}
                onChange={e => setProductUrls(e.target.value)}
                disabled={busy}
                rows={6}
                placeholder={"https://offroadanimal.com.au/rear-bar-to-suit-ranger-raptor-next-gen-2022-on/\nhttps://offroadanimal.com.au/rear-bumper-and-tow-bar-isuzu-dmax-my21-2021-to-2023/"}
                className="w-full bg-background border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-emerald-500/40 disabled:opacity-50 font-mono resize-y"
              />
              <p className="text-xs text-muted-foreground">
                One URL per line. Use this for products that don't have a category page (e.g. rear bars).
              </p>
            </div>
          )}

          <div className="space-y-2">
            {stores.length === 0 ? (
              <p className="text-sm text-muted-foreground">No stores connected.</p>
            ) : (
              <ShopifyTargetStoreSelector
                stores={stores}
                value={storeId}
                onChange={setStoreId}
                label="Destination Shopify Store"
                variant="tiles"
                disabled={busy}
              />
            )}
          </div>

          <div className="flex items-center gap-3">
            {!busy ? (
              <Button
                onClick={startRun}
                disabled={!storeId || (inputMode === 'category' ? !categoryUrl.trim() : !productUrls.trim())}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Search className="w-4 h-4 mr-2" />
                Scrape &amp; Update Prices
              </Button>
            ) : (
              <button
                onClick={cancel}
                className="text-sm text-muted-foreground hover:text-foreground border border-border rounded-xl px-4 py-2 transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
        </div>

        {/* Progress */}
        {busy && (
          <div className="glass-panel rounded-2xl p-5 space-y-4">

            {/* Step indicators */}
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className={cn("font-medium", (phase === 'discovery' || phase === 'scraping') ? "text-emerald-400" : "text-muted-foreground/40")}>
                1 Scraping prices
              </span>
              <span className="mx-1">→</span>
              <span className={cn("font-medium", phase === 'pushing' ? "text-emerald-400" : "text-muted-foreground/40")}>
                2 Updating Shopify
              </span>
            </div>

            {(phase === 'discovery' || phase === 'scraping') && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 text-emerald-400 animate-spin flex-shrink-0" />
                  <span className="text-sm text-foreground">
                    {phase === 'discovery'
                      ? `Scanning category — page ${run.discoveryPage}${run.scrapeFound > 0 ? `, ${run.scrapeFound} products found` : ''}…`
                      : `Fetching prices — ${run.scrapeProducts} / ${run.scrapeTotal} products (${run.scrapeFound} prices collected)`
                    }
                  </span>
                </div>
                {phase === 'scraping' && run.scrapeTotal > 0 && (
                  <div className="w-full bg-muted rounded-full h-1.5">
                    <div className="bg-emerald-500 h-1.5 rounded-full transition-all" style={{ width: `${scrapePercent}%` }} />
                  </div>
                )}
              </div>
            )}

            {phase === 'pushing' && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 text-emerald-400 animate-spin flex-shrink-0" />
                  <span className="text-sm text-foreground">
                    Updating {storeName} — {run.pushCurrent} / {run.pushTotal} variants
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-1.5">
                  <div className="bg-emerald-500 h-1.5 rounded-full transition-all" style={{ width: `${pushPercent}%` }} />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Error */}
        {phase === 'error' && (
          <div className="glass-panel rounded-2xl p-5 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">Something went wrong</p>
              <p className="text-xs text-muted-foreground mt-1">{run.errorMessage}</p>
            </div>
            <button
              onClick={() => setRun(IDLE_STATE)}
              className="text-xs text-muted-foreground hover:text-foreground border border-border rounded-lg px-3 py-1.5 flex-shrink-0"
            >
              <RotateCw className="w-3 h-3 mr-1 inline" /> Retry
            </button>
          </div>
        )}

        {/* Done */}
        {phase === 'done' && (
          <div className="space-y-4">
            <div className="glass-panel rounded-2xl p-5">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-emerald-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-foreground">
                    Prices updated in {storeName}
                  </p>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2">
                    <span className="text-sm text-emerald-400 font-medium">{run.pushUpdated} variants updated</span>
                    <span className="text-sm text-muted-foreground">{run.totalVariants} scraped across {run.totalProducts} products</span>
                    {run.pushSkipped > 0 && <span className="text-sm text-muted-foreground">{run.pushSkipped} not matched in Shopify</span>}
                    {run.pushErrors.length > 0 && <span className="text-sm text-red-400">{run.pushErrors.length} errors</span>}
                  </div>
                </div>
                <button
                  onClick={() => setRun(IDLE_STATE)}
                  className="text-xs text-muted-foreground hover:text-foreground border border-border rounded-lg px-3 py-1.5 flex-shrink-0"
                >
                  <RotateCw className="w-3 h-3 mr-1 inline" /> Run again
                </button>
              </div>

              {run.pushErrors.length > 0 && (
                <div className="mt-4 space-y-1 max-h-28 overflow-y-auto">
                  {run.pushErrors.map((e, i) => (
                    <div key={i} className="text-xs text-red-400 bg-red-500/10 rounded-lg px-3 py-1.5">
                      <span className="font-mono">{e.sku}</span>: {e.error}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Price preview table */}
            {previewEntries.length > 0 && (
              <div className="glass-panel rounded-2xl overflow-hidden">
                <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-foreground">Scraped prices</h3>
                  <span className="text-xs text-muted-foreground">
                    {previewEntries.length < run.totalVariants
                      ? `Showing ${previewEntries.length} of ${run.totalVariants}`
                      : `${run.totalVariants} total`}
                  </span>
                </div>
                <div className="overflow-x-auto max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 sticky top-0">
                      <tr>
                        <th className="text-left px-5 py-2.5 text-xs font-medium text-muted-foreground">SKU</th>
                        <th className="text-left px-5 py-2.5 text-xs font-medium text-muted-foreground">Options</th>
                        <th className="text-right px-5 py-2.5 text-xs font-medium text-muted-foreground">RRP (AUD)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewEntries.map(([key, price], i) => {
                        const { sku, options } = parseVariantKey(key);
                        return (
                          <tr key={key} className={cn("border-t border-border/40", i % 2 !== 0 && "bg-muted/20")}>
                            <td className="px-5 py-2 font-mono text-xs text-foreground whitespace-nowrap">{sku}</td>
                            <td className="px-5 py-2">
                              {options.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {options.map((opt, j) => (
                                    <span key={j} className="text-[10px] px-1.5 py-0.5 rounded bg-muted/60 text-muted-foreground border border-border/40">
                                      {opt}
                                    </span>
                                  ))}
                                </div>
                              ) : <span className="text-xs text-muted-foreground/40">—</span>}
                            </td>
                            <td className="px-5 py-2 text-right text-emerald-400 font-medium whitespace-nowrap">
                              ${parseFloat(price).toFixed(2)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
